import { defineStore } from "pinia";
import { markRaw, watchEffect } from "vue";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { resolveBreakpointForWidth } from "%/dynamic-component-editor/helpers/breakpoint-ranges";
import { PerformanceTimer } from "@vcl";

export const useDynamicComponentDomStateStore = defineStore("dynamic-component-dom-state-store", {
  state: () => ({
    pointerEnter: [],
    breakpointListeners: {},
    activeBreakpoints: {},
    // Optional injected callbacks the breakpoint observer reads on
    // every resize check. The page-level consumer (Staging.vue,
    // Production.vue) registers these so the observer can pick up
    // breakpoints from a destination context that the global
    // configuration store doesn't otherwise know about. Stored as
    // raw (non-reactive) so Pinia doesn't try to deep-proxy the
    // function — we don't want the function itself to be reactive,
    // only the values it returns.
    breakpointsSource: null,
    designVariantSource: null,
  }),
  getters: {
    // Determine the current design variant. Prefer an explicit source
    // function (set by `setBreakpointsSource`) so destination-level
    // overrides win over the publication-wide configuration store.
    designVariant() {
      const fromSource = this.designVariantSource?.();
      return fromSource || useConfigurationStore().designVariant || "desktop";
    },
  },
  actions: {
    // Register a destination-aware breakpoints + designVariant source.
    // The breakpoint observer reads from these callbacks on every
    // resize check (no copy / no watcher), so any reactive change to
    // the underlying source (e.g. a destination's
    // `DestinationConfiguration.breakpoints` finally hydrating) flows
    // through automatically on the next resize.
    setBreakpointsSource(breakpointsFn, designVariantFn = null) {
      this.breakpointsSource = breakpointsFn ? markRaw(breakpointsFn) : null;
      this.designVariantSource = designVariantFn ? markRaw(designVariantFn) : null;
    },
    setBreakpointListener(element, identifier) {
      if (!identifier) {
        return;
      }
      if (this.breakpointListeners[identifier]) {
        return;
      }

      const setupTimer = PerformanceTimer.startTimer(
        `breakpoint:setup:${identifier}`,
        "breakpoint-detection",
      );

      // The "default" identifier (production / staging render) tracks
      // the VIEWPORT, not the element it was passed. Observing the
      // element would tie the breakpoint to whatever `contentRect`
      // that element reports — and any layout where the root
      // container has an intrinsic width (`width="hug"` /
      // `fit-content`) makes the wrapper expand past the viewport,
      // so the observer never sees the viewport shrink and
      // breakpoint detection silently freezes. Editor canvas frames
      // (alias=tablet/phone/etc.) keep using element-bound
      // observation because each frame is sized to the breakpoint
      // itself.
      const observerSource = identifier === "default" ? "viewport" : "element";

      this.breakpointListeners[identifier] = this.createBreakpointObserver(element, breakpoint => {
        const detectTimer = PerformanceTimer.startTimer(
          `breakpoint:detect:${identifier}:${breakpoint?.alias || "none"}`,
          "breakpoint-detection",
        );

        this.activeBreakpoints[identifier] = breakpoint ? breakpoint.alias : "";

        PerformanceTimer.stopTimer(detectTimer);
      }, observerSource);

      PerformanceTimer.stopTimer(setupTimer);
    },
    disconnect() {
      Object.values(this.breakpointListeners).forEach(disconnectFn => {
        if (typeof disconnectFn === "function") {
          disconnectFn();
        }
      });
      this.breakpointListeners = {};
    },
    createBreakpointObserver(element, callback, observerSource = "element") {
      let currentBreakpoint = null;

      // Reading breakpoints once at observer-creation time captured an
      // empty array on first mount in `/stagingstudio/...` (the
      // destination's breakpoints hadn't hydrated yet) and the
      // observer never noticed when they arrived. Reading from the
      // configuration store inside `checkBreakpoint` instead means
      // every resize also picks up any post-mount hydration of
      // `dynamicComponentBreakpoints`.
      const readBreakpoints = () => {
        // Prefer the page-injected source (Staging/Production register
        // a destination-bound function via `setBreakpointsSource`).
        // Falls back to the publication-wide configuration store for
        // call sites that haven't registered a source — the editor
        // canvas + the legacy preview paths.
        const fromSource = this.breakpointsSource?.();
        const raw = fromSource && fromSource.length
          ? fromSource
          : useConfigurationStore().dynamicComponentBreakpoints ?? [];
        return raw;
      };

      const checkBreakpoint = width => {
        // Shared with the canvas ruler — see helpers/breakpoint-ranges.
        const newBreakpoint = resolveBreakpointForWidth(width, readBreakpoints(), this.designVariant);

        // Only trigger callback if breakpoint changed (compare by alias —
        // same alias re-resolved through a fresh map is still the same
        // breakpoint, even though the object identity differs).
        const newAlias = newBreakpoint?.alias ?? null;
        const currentAlias = currentBreakpoint?.alias ?? null;
        if (newAlias !== currentAlias) {
          currentBreakpoint = newBreakpoint;
          callback(newBreakpoint, width);
        }
      };

      // Viewport-bound: read `window.innerWidth` and listen to the
      // window's `resize` event. Native, doesn't care what's nested
      // inside the page, can't be defeated by a child element with
      // intrinsic width. This is the right axis for staging /
      // production breakpoint detection — the user's browser
      // viewport is what they're sizing against.
      //
      // The first check is wrapped in a `watchEffect` so that
      // `checkBreakpoint`'s reactive reads (the registered
      // `breakpointsSource` callback, which itself walks the
      // destination's Pinia getter chain) are tracked. When those deps
      // hydrate after observer creation — e.g. the destination feed
      // resolves and `breakpointsSource()` flips from `[]` to a real
      // list — the effect re-runs and `activeBreakpoints[identifier]`
      // updates without waiting for a real `resize` event. Without
      // this, registering a source after the listener was set up left
      // `activeBreakpoints` stuck at the empty initial detection until
      // the user manually resized the viewport.
      if (observerSource === "viewport") {
        const onResize = () => checkBreakpoint(window.innerWidth);
        const stopReactiveCheck = watchEffect(onResize);
        window.addEventListener("resize", onResize);
        return () => {
          stopReactiveCheck();
          window.removeEventListener("resize", onResize);
        };
      }

      // Element-bound: original behaviour, used by editor canvas
      // frames whose parentElIdentifier carries the breakpoint
      // alias (e.g. `tablet`). Each frame is sized to the
      // breakpoint, so observing its width still produces the
      // correct active alias even though it's a per-frame ResizeObserver.
      checkBreakpoint(element.offsetWidth);

      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          checkBreakpoint(width);
        }
      });

      observer.observe(element);

      // Return cleanup function
      return () => observer.disconnect();
    },
  },
});
